import { isoToZonedParts } from '@wit-support/ui';
import { currencyScale, formatMoney, minorToMajor } from '@wit-support/ui-domain';
import { ADS_BUILDER_TIME_ZONE } from '@wit-support/admin-contracts';
/**
 * Пояс ВСЕГО времени конструктора (v3 §5): ввод «В дату», «По нулям», час
 * кабинета и показ старта — по Лос-Анджелесу, а не по поясу кабинета.
 */
export const ADS_TZ = ADS_BUILDER_TIME_ZONE;
/** Подпись пояса у полей ввода времени. */
export const ADS_TZ_INPUT_LABEL = 'время Лос-Анджелеса';
/**
 * Деньги приходят в minor units. Показ — общий `formatMoney` (ui-domain: одна
 * таблица знаков валют и локаль валюты на всю админку). Нет суммы — «—»; без
 * валюты — число с двумя знаками.
 */
export function formatMinor(minor, currency) {
    if (minor == null || !Number.isFinite(minor))
        return '—';
    if (currency)
        return formatMoney(minor, currency);
    const digits = currencyScale(null);
    return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(minorToMajor(minor, null));
}
/** Сумма по валютам: «$442.37 + €10.00». */
export function formatMinorByCurrency(byCurrency) {
    const parts = Object.entries(byCurrency).map(([cur, v]) => formatMinor(v, cur));
    return parts.length > 0 ? parts.join(' + ') : '—';
}
export function formatPercent(loaded, total) {
    if (total <= 0)
        return '0 %';
    return `${Math.min(100, Math.floor((loaded / total) * 100))} %`;
}
/** Формы для `pluralRu` (`@wit-support/ui`) — глагол в лад с числом: «Будет создана 1 кампания» / «Будут созданы 2 кампании» / «Будет создано 5 кампаний». */
export const WILL_BE_CREATED_F = ['Будет создана', 'Будут созданы', 'Будет создано'];
export const WILL_BE_EXECUTED_M = ['Будет выполнен', 'Будут выполнены', 'Будет выполнено'];
// --- время: всё по Лос-Анджелесу ------------------------------------------
/** Пояса, которые встречаются у кабинетов, — по-русски; остальные — город из имени пояса. */
const TZ_RU = {
    UTC: 'UTC',
    'Etc/UTC': 'UTC',
    'America/Los_Angeles': 'Лос-Анджелес',
    'America/New_York': 'Нью-Йорк',
    'America/Chicago': 'Чикаго',
    'America/Denver': 'Денвер',
    'America/Phoenix': 'Финикс',
    'America/Sao_Paulo': 'Сан-Паулу',
    'America/Mexico_City': 'Мехико',
    'America/Toronto': 'Торонто',
    'Europe/London': 'Лондон',
    'Europe/Berlin': 'Берлин',
    'Europe/Paris': 'Париж',
    'Europe/Madrid': 'Мадрид',
    'Europe/Moscow': 'Москва',
    'Europe/Kiev': 'Киев',
    'Europe/Kyiv': 'Киев',
    'Europe/Warsaw': 'Варшава',
    'Europe/Istanbul': 'Стамбул',
    'Asia/Dubai': 'Дубай',
    'Asia/Bangkok': 'Бангкок',
    'Asia/Ho_Chi_Minh': 'Хошимин',
    'Asia/Singapore': 'Сингапур',
    'Asia/Tokyo': 'Токио',
    'Asia/Kolkata': 'Калькутта',
    'Asia/Jakarta': 'Джакарта',
    'Australia/Sydney': 'Сидней',
};
/** «America/Los_Angeles» → «Лос-Анджелес»; незнакомый пояс — город из имени («Port Moresby»). */
export function tzLabelRu(timeZone) {
    if (!timeZone)
        return 'UTC';
    const known = TZ_RU[timeZone];
    if (known)
        return known;
    const city = timeZone.split('/').pop() ?? timeZone;
    return city.replace(/_/g, ' ');
}
/**
 * Части даты-времени момента в поясе: «14.10.2026», «03:00». Перевод — общий
 * `isoToZonedParts` кита (`@wit-support/ui`); null — не дата или пояс неизвестен.
 */
function partsInZone(iso, timeZone) {
    const p = isoToZonedParts(iso, timeZone);
    if (!p)
        return null;
    const [y, m, d] = p.date.split('-');
    return { date: `${d}.${m}.${y}`, time: p.time };
}
/** «14.10.2026» → «14.10»: год у старта запуска лишний (старт — ближайшие дни). */
function shortDate(date) {
    return date.slice(0, 5);
}
/**
 * Момент в поясе — ОДИН формат на весь раздел (план, пачка,
 * запуск, подтверждение): «14.10 03:00 Лос-Анджелес». Пояс неизвестен — UTC.
 */
export function formatMomentInZone(iso, timeZone) {
    const tz = timeZone || 'UTC';
    const p = partsInZone(iso, tz) ?? partsInZone(iso, 'UTC');
    if (!p)
        return iso;
    return `${shortDate(p.date)} ${p.time} ${tzLabelRu(tz)}`;
}
/**
 * Окно моментов в одном поясе: «14.10 03:00–03:50 Лос-Анджелес», через
 * сутки — обе даты. Один момент — как `formatMomentInZone`.
 */
export function formatWindowInZone(fromIso, toIso, timeZone) {
    const tz = timeZone || 'UTC';
    const a = partsInZone(fromIso, tz);
    const b = partsInZone(toIso, tz);
    if (!a || !b)
        return formatMomentInZone(fromIso, tz);
    const head = `${shortDate(a.date)} ${a.time}`;
    if (a.date === b.date && a.time === b.time)
        return `${head} ${tzLabelRu(tz)}`;
    const tail = a.date === b.date ? b.time : `${shortDate(b.date)} ${b.time}`;
    return `${head}–${tail} ${tzLabelRu(tz)}`;
}
const LOCAL_FULL_RE = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/;
const LOCAL_TIME_RE = /^(\d{1,2}):(\d{2})$/;
/**
 * Старт запуска — всегда по Лос-Анджелесу: «14.10 03:00 Лос-Анджелес».
 * Готовое время auto-ads (`startTimeLocal` + `startTimeZone`) главнее; без
 * `startTimeZone` (старый auto-ads клал туда время кабинета) — момент
 * `startTime` переводится в Лос-Анджелес сам. null — старта нет.
 */
export function formatStartInZone(s) {
    if (!s.startTimeZone && s.startTime && !Number.isNaN(Date.parse(s.startTime))) {
        return formatMomentInZone(s.startTime, ADS_TZ);
    }
    const tz = s.startTimeZone || ADS_TZ;
    const local = s.startTimeLocal?.trim() ?? '';
    const full = LOCAL_FULL_RE.exec(local);
    if (full)
        return `${full[3]}.${full[2]} ${full[4]}:${full[5]} ${tzLabelRu(tz)}`;
    const time = LOCAL_TIME_RE.exec(local);
    if (time) {
        const hhmm = `${time[1].padStart(2, '0')}:${time[2]}`;
        const p = s.startTime && tz ? partsInZone(s.startTime, tz) : null;
        return `${p ? `${shortDate(p.date)} ` : ''}${hhmm} ${tzLabelRu(tz)}`;
    }
    if (s.startTime) {
        if (Number.isNaN(Date.parse(s.startTime)))
            return local !== '' ? local : s.startTime;
        return formatMomentInZone(s.startTime, tz);
    }
    return local !== '' ? `${local} ${tzLabelRu(tz)}` : null;
}
/** Настенное время «дата + часы:минуты» как число — только для сравнения. */
function wallClockKey(date, time) {
    const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    const t = /^(\d{1,2}):(\d{2})/.exec(time);
    if (!d || !t)
        return null;
    return Date.UTC(+d[1], +d[2] - 1, +d[3], +t[1], +t[2]);
}
function momentKey(iso, tz) {
    const p = isoToZonedParts(iso, tz) ?? isoToZonedParts(iso, 'UTC');
    return p ? wallClockKey(p.date, p.time) : null;
}
/**
 * Ключ сортировки старта — ровно то, что показывает `formatStartInZone`
 * (те же ветки): настенное время в показанном поясе. null — старта нет или
 * показан непонятный текст (сортировать нечем).
 */
export function startSortKey(s) {
    if (!s.startTimeZone && s.startTime && !Number.isNaN(Date.parse(s.startTime))) {
        return momentKey(s.startTime, ADS_TZ);
    }
    const tz = s.startTimeZone || ADS_TZ;
    const local = s.startTimeLocal?.trim() ?? '';
    const full = LOCAL_FULL_RE.exec(local);
    if (full)
        return Date.UTC(+full[1], +full[2] - 1, +full[3], +full[4], +full[5]);
    const time = LOCAL_TIME_RE.exec(local);
    if (time) {
        const p = s.startTime ? isoToZonedParts(s.startTime, tz) : null;
        return wallClockKey(p?.date ?? '1970-01-01', `${time[1]}:${time[2]}`);
    }
    if (s.startTime && !Number.isNaN(Date.parse(s.startTime)))
        return momentKey(s.startTime, tz);
    return null;
}
/**
 * Ключ идемпотентности новой пачки: 8–64 символа `[A-Za-z0-9_-]`. Один ключ
 * на экран «Новый запуск» — повторное нажатие «Создать» не создаёт вторую
 * пачку (409 `idempotency-key-taken` с id первой).
 */
export function newIdempotencyKey() {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function')
        return `ui_${c.randomUUID().replace(/-/g, '')}`;
    let s = 'ui_';
    for (let i = 0; i < 24; i++)
        s += Math.floor(Math.random() * 36).toString(36);
    return s;
}
//# sourceMappingURL=format.js.map